import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactVisibilitySensor from 'react-visibility-sensor';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { addAvaiabilityUnvaible } from 'redux/Reducers/Showcases/ShowcaseAutomateReducer';
import { addToWishListThunk, removeFromWishListThunk } from 'redux/Reducers/WishList/WishListSlice';

import { WishListContext } from 'Context/Wishlist';

// DS
import { notification, VertCardProduct } from '@vertem-core/vertice-ds';

// SERVICES
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';
import { pointsMask } from 'Utils/commons/points';
import { LoadingOutlined } from '@ant-design/icons';

import 'Assets/styles/default/components/Card/cardStyle.less';
import { useRating } from "components/RatingProducts/Provider";
import useGA from "Utils/Ga";

class Beholder {
  static instance;
  constructor() {
    if (Beholder.instance) return Beholder.instance;
    this.skusListAvailability = new Set();
    Beholder.instance = this;
  }

  addAvailabilityCall(sku) {
    this.skusListAvailability.add(sku);
  }

  availabiltyWasCalled(sku) {
    return this.skusListAvailability.has(sku);
  }
}

const VertCardProductContainer = ({ cardProduct, iconEmpty, iconFilled }) => {
  const Status = new Beholder();
  
  const { eventGaUnlimited } = useGA()
  const { simpleWishList, updateSimpleWishList } = useContext(WishListContext);
  
  const [loadingWishList, setLoadingWishList] = useState(false);
  const [availabilityInit, setAvailabilityInit] = useState(null);
  const [Availability, setAvailability] = useState({});
  const { callApi, callApiForce } = usePipeContext();
  const [availabilityStatic, setAvailabilityStatic] = useState({});
  const [tagsItems, setTagsItems] = useState({});
  const [tagData, setTagData] = useState({});
  const productType = get(cardProduct, 'type', 1);
  const [callRating, setCallRating] = useState(false);
  const { isEnabled, ratingList, getRatingBySku, configRating } = useRating();
  
  const dispatch = useDispatch();

  const Tag = useSelector(({ Tag: data }) => data);
  const AvailabilityData = useSelector(({ Availability: data }) => data);
  const Wishlist = useSelector(({ SimpleWishlist: data }) => data);
  const Catalog = useSelector(({ Catalog: data }) => data);
  /* States */
  const [objectCardProps, setObjectCardProps] = useState({
    cardProps: { isLoading: true },
  });

  /* Vars */
  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;

  const hasPriceUpdated = get(cardProduct, 'hasPriceUpdated');

  const productValues = hasPriceUpdated
    ? {
        price: cardProduct.availability.price,
        regularPrice: cardProduct.availability.regularPrice,
      }
    : Availability[cardProduct?.sku] && Availability[cardProduct?.sku]?.price;

  const formattedValue = pointsMask(
    productValues?.price,
    decimalPlaces,
    pointsName
  );

  const formattedRegularValue = pointsMask(
    productValues?.regularPrice,
    decimalPlaces,
    pointsName
  );

  const isInWishList = simpleWishList.get(cardProduct.sku);

  /* Utils */
  const priceProps = {
    price: formattedValue.value,
    regularPrice: formattedRegularValue.value,
    currency: formattedValue.label || formattedRegularValue.label,
  };

  const discountFlag = (() => {
    const available = Availability[cardProduct.sku]?.isAvailable;
    const discountRate = Availability[cardProduct.sku]?.price?.discountRate;
    const staticAvailable = availabilityStatic[cardProduct.sku]?.available;
    const staticDiscountRate =
      availabilityStatic[cardProduct.sku]?.discountRate;

    const hasValidDiscount = (rate) => rate && rate !== 0;

    if (available && hasValidDiscount(discountRate)) {
      return [
        {
          label: `${discountRate}%`,
          backgroundColor: '#F62D51',
          textColor: '#ffffff',
        },
      ];
    }

    if (staticAvailable && hasValidDiscount(staticDiscountRate)) {
      return [
        {
          label: `${staticDiscountRate}%`,
          backgroundColor: '#F62D51',
          textColor: '#ffffff',
        },
      ];
    }

    return [];
  })();

  const customFlags = tagsItems[cardProduct.sku]
    ? tagsItems[cardProduct.sku].data || []
    : [];

  const handleSetAvailabilityInit = useCallback(
    (status) => {
      if (availabilityInit === null) {
        setAvailabilityInit(status);
      }
    },
    [availabilityInit]
  );

  const vertCardProductProps = {
    ...cardProduct,
    productUrl: `/product/${cardProduct.sku}`,
    flags: [...discountFlag, ...customFlags],
    hasWishlist: isInWishList,
  };

  const loadExtras = () => {
    callApi({
      symbol: 'FETCH_TAGS',
      alias: `$tag: ${cardProduct.sku}`,
      payload: {
        sku: cardProduct.sku,
        callback: (response) => {
          setTagData(response);
        },
      },
    });
  };

  const handleWishListToggle = async (productSku) => {
    setLoadingWishList(true);

    try {
      const hasProductInWishList = simpleWishList.get(productSku);

      hasProductInWishList
        ? await dispatch(removeFromWishListThunk(productSku)).unwrap()
        : await dispatch(addToWishListThunk(productSku)).unwrap();

      if (!hasProductInWishList) {
        eventGaUnlimited("add_to_wishlist", {
          currency: Catalog?.currencyConfiguration?.pointsName?.toUpperCase(),
          value: parseFloat(priceProps.price),
          items: [
            {
              item_id: cardProduct.sku,
              item_name: cardProduct.name,
              currency: Catalog?.currencyConfiguration?.pointsName?.toUpperCase(),
              item_brand: cardProduct?.vendor?.name || cardProduct?.vendorName,
              item_list_id: "related_products",
              item_list_name: cardProduct.showcaseName,
              price: parseFloat(priceProps.price),
              quantity: 1
            }
          ]
        });
      }

      await updateSimpleWishList();
      
      setLoadingWishList(false);
    } catch (err) {
      console.log(err);

      setLoadingWishList(false);

      notification.error({
        message: "Houve um erro ao favoritar produto, tente novamente mais tarde.",
      });
    }
  }

  /* Lifecycles */
  useEffect(() => {
    if (
      productType == 3 ||
      (Availability[cardProduct.sku] &&
        Availability[cardProduct.sku].isAvailable)
    ) {
      dispatch(addAvaiabilityUnvaible(cardProduct.sku));
    }
  }, [
    Availability[cardProduct?.sku]?.isAvailable,
    Availability[cardProduct?.sku]?.price,
  ]);

  useEffect(() => {
    const isAvailable =
      productType == 3 ||
      (Availability[cardProduct.sku] &&
        Availability[cardProduct.sku]?.isAvailable);
    const isLoading = Availability[cardProduct.sku]?.isLoading;

    if (!isAvailable) loadExtras();

    if (
      (isAvailable === false || isLoading === false) &&
      Availability[cardProduct.sku]
    ) {
      setObjectCardProps({
        cardProps: {
          isLoading: false,
        },
      });
    } else {
      setObjectCardProps({
        cardProps: {
          isLoading: true,
        },
      });
    }
  }, [
    Availability[cardProduct.sku],
    Availability[cardProduct?.sku]?.isAvailable,
  ]);

  useEffect(() => {
    if (
      !hasPriceUpdated &&
      cardProduct.type != 3 &&
      !availabilityInit &&
      availabilityInit !== null &&
      cardProduct &&
      cardProduct.sku &&
      (cardProduct?.vendor?.id || cardProduct.vendorId) &&
      cardProduct.originalId &&
      !Status.availabiltyWasCalled(cardProduct.sku)
    ) {
      Status.addAvailabilityCall(cardProduct.sku);
      callApiForce({
        symbol: 'FETCH_AVAILABILITY',
        alias: null,
        payload: {
          originalId: cardProduct.originalId,
          vendorId: cardProduct?.vendor?.id || cardProduct.vendorId,
          sku: cardProduct.sku,
          callback: (response) => {
            if (response === null) return;
            const { data } = response;
            const skuData = {
              discountRate: data[0].cash.discountRateCash,
              discountRateCash: data[0].cash.discountRateCash,
              isAvailable: data[0].available,
              isAvailableForParticipant: data[0].isAvailableForParticipant,
              isLoading: false,
              isPriceUpdated: true,
              price: data[0],
            };
            setAvailabilityStatic({
              [cardProduct.sku]: skuData,
            });
          },
        },
      });
      setAvailabilityInit(true);
    }
  }, [cardProduct, availabilityInit, hasPriceUpdated]);

  useEffect(() => {
    if (hasPriceUpdated) {
      setAvailabilityStatic({
        [cardProduct.sku]: {
          ...cardProduct.availability,
          isAvailable: cardProduct.availability ? true : false,
          isLoading: false,
          isPriceUpdated: true,
          price: cardProduct.availability.price,
        },
      });

      setObjectCardProps({
        cardProps: {
          isLoading: false,
        },
      });

      return;
    }

    if (
      AvailabilityData[cardProduct?.sku] &&
      !availabilityStatic[cardProduct?.sku]?.isAvailable &&
      ((AvailabilityData[cardProduct?.sku]?.discountRate !== undefined &&
        AvailabilityData[cardProduct?.sku]?.discountRate !== null) ||
        (!AvailabilityData[cardProduct?.sku]?.isAvailable &&
          !AvailabilityData[cardProduct?.sku]?.isAvailable.isLoading))
    ) {
      setAvailability({ ...AvailabilityData });
    } else if (
      availabilityStatic &&
      Object.keys(availabilityStatic).length > 0
    ) {
      setAvailability(availabilityStatic);
    }
  }, [AvailabilityData, availabilityStatic]);

  useEffect(() => {
    if (Tag && Tag[cardProduct.sku] && Tag[cardProduct.sku]?.data.length) {
      setTagsItems(Tag);
    } else if (tagData && tagData[cardProduct.sku]) {
      setTagsItems(tagData);
    }
  }, [tagData, Tag]);

  useEffect(() => {
    if (
      !Object.keys(AvailabilityData).length ||
      !AvailabilityData[cardProduct.sku] ||
      (AvailabilityData[cardProduct.sku] &&
        !AvailabilityData[cardProduct.sku]?.isLoaded)
    ) {
      handleSetAvailabilityInit(false);
    }
  }, [AvailabilityData]);

  useEffect(() => {
    if (
      isEnabled &&
      cardProduct.sku &&
      !ratingList[cardProduct.sku] &&
      getRatingBySku &&
      !callRating
    ) {
      getRatingBySku(cardProduct.sku);
      setCallRating(true);
    }
  }, [isEnabled, ratingList, cardProduct, callRating]);

  /* Renders */
  if (!cardProduct) return null;
  return (
    <ReactVisibilitySensor
      intervalDelay={300}
      delayedCall={true}
      intervalCheck={true}
      offset={{ top: 50, left: 50, right: 50, bottom: 50 }}
      onChange={(isVisible) => isVisible && loadExtras()}
    >
      <div>
        <VertCardProduct
          {...vertCardProductProps}
          {
            ...objectCardProps
          }
          {
            ...(() => {
              if (!ratingList[cardProduct.sku] || configRating && configRating.minimumRatingQuantity > ratingList[cardProduct.sku].rating) return {}
              return {
                rating: {
                  average: ratingList[cardProduct.sku].rating,
                  totalCount: 5 // TODO: Verificar se o total é mutavel
                }
              }
            })()
          }
          price={priceProps}
          available={
            productType == 3 ||
            (AvailabilityData[cardProduct.sku] &&
              AvailabilityData[cardProduct.sku].isAvailable &&
              AvailabilityData[cardProduct.sku].isAvailableForParticipant)
          }
          vendor={cardProduct?.vendor?.name || cardProduct?.vendorName}
          iconEmpty={!loadingWishList ? iconEmpty : <LoadingOutlined />}
          iconFilled={!loadingWishList ? iconFilled : <LoadingOutlined />}
          wishlistHandler={() => handleWishListToggle(cardProduct.sku)}
          hasWishlist={isInWishList}
          isQuoteItem={productType == 3}
        />
      </div>
    </ReactVisibilitySensor>
  );
};

VertCardProductContainer.propTypes = {
  cardProduct: PropTypes.object.isRequired,
  flags: PropTypes.array,
  key: PropTypes.string,
  iconEmpty: PropTypes.node,
  iconFilled: PropTypes.node,
  wishlistHandler: PropTypes.func,
  isVoucher: PropTypes.bool,
  itemListId: PropTypes.string,
  itemListName: PropTypes.string,
};

export default VertCardProductContainer;
